import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useI18n } from '@bravemove/i18n/react';
import {
  Badge,
  Banner,
  Button,
  FormField,
  Spinner,
  Table,
  TextInput,
  type TableColumn,
} from '@bravemove/ui-web';
import { adminFetch, buildQuery } from '../auth/apiClient';
import { type AdminAuditEntry, type ListEnvelope } from '../auth/types';
import { useAuth } from '../auth/useAuth';
import { translateApiError } from '../auth/errors';
import { PageHeader } from '../components/PageHeader';

const PAGE_SIZE = 25;

interface FilterValues {
  readonly action: string;
  readonly targetType: string;
  readonly actor: string;
  readonly from: string;
  readonly to: string;
}

const EMPTY_FILTERS: FilterValues = {
  action: '',
  targetType: '',
  actor: '',
  from: '',
  to: '',
};

/**
 * S-14 — the backend validates `actor` as `z.string().uuid()`; free text (an
 * email is the natural guess) would 422 and unhelpfully blank the table. Gate
 * client-side with the same shape so the admin gets a field-level error
 * instead.
 */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function AuditLogPage() {
  const { t, locale } = useI18n();
  const { adminUser } = useAuth();
  const isSuperAdmin = adminUser?.role === 'super_admin';

  const [rows, setRows] = useState<readonly AdminAuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [filters, setFilters] = useState<FilterValues>(EMPTY_FILTERS);

  const form = useForm<FilterValues>({ defaultValues: EMPTY_FILTERS });

  const fetchAudit = useCallback(
    async (nextOffset: number, f: FilterValues) => {
      setLoading(true);
      setError(null);
      try {
        const body = await adminFetch<ListEnvelope<AdminAuditEntry>>(
          `/admin/audit-log${buildQuery({
            limit: PAGE_SIZE,
            offset: nextOffset,
            action: f.action,
            targetType: f.targetType,
            actor: isSuperAdmin ? f.actor.trim() : undefined,
            from: f.from ? new Date(f.from).toISOString() : undefined,
            to: f.to ? new Date(f.to).toISOString() : undefined,
          })}`,
        );
        setRows(body.items);
        setOffset(body.offset);
        setHasMore(body.items.length === PAGE_SIZE);
      } catch (err) {
        setError(translateApiError(err, t));
      } finally {
        setLoading(false);
      }
    },
    [t, isSuperAdmin],
  );

  useEffect(() => {
    // Fetch-on-mount; setState happens inside fetchAudit which is the
    // canonical async-load pattern.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchAudit(0, EMPTY_FILTERS);
  }, [fetchAudit]);

  const onApplyFilters = form.handleSubmit((values) => {
    setFilters(values);
    void fetchAudit(0, values);
  });

  const onClearFilters = () => {
    form.reset(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
    void fetchAudit(0, EMPTY_FILTERS);
  };

  const columns = useMemo<readonly TableColumn<AdminAuditEntry>[]>(
    () => [
      {
        key: 'when',
        header: t('admin:audit.table.when'),
        cell: (row) => (
          <span className="font-mono text-[12px] text-ink">
            {formatDate(row.createdAt, locale)}
          </span>
        ),
      },
      {
        key: 'actor',
        header: t('admin:audit.table.actor'),
        cell: (row) => row.actorEmail,
      },
      {
        key: 'action',
        header: t('admin:audit.table.action'),
        cell: (row) => <span className="font-mono text-[12px] text-accent">{row.action}</span>,
      },
      {
        key: 'target',
        header: t('admin:audit.table.target'),
        cell: (row) =>
          row.targetType ? (
            <span className="font-mono text-[12px] text-ink-2">
              {row.targetType}
              {row.targetId ? `:${row.targetId.slice(0, 8)}` : ''}
            </span>
          ) : (
            '—'
          ),
      },
      {
        key: 'status',
        header: t('admin:audit.table.status'),
        cell: (row) => (
          <Badge tone={row.status === 'success' ? 'success' : 'danger'}>
            {t(`admin:audit.status.${row.status}`)}
          </Badge>
        ),
      },
    ],
    [t, locale],
  );

  return (
    <div className="space-y-6">
      <PageHeader title={t('admin:audit.title')} subtitle={t('admin:audit.subtitle')} />

      <form
        onSubmit={onApplyFilters}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3"
      >
        <FormField label={t('admin:audit.filters.action')}>
          <TextInput
            placeholder={t('admin:audit.filters.actionPlaceholder')}
            {...form.register('action')}
          />
        </FormField>
        <FormField label={t('admin:audit.filters.targetType')}>
          <TextInput
            placeholder={t('admin:audit.filters.targetTypePlaceholder')}
            {...form.register('targetType')}
          />
        </FormField>
        {isSuperAdmin ? (
          <FormField
            label={t('admin:audit.filters.actor')}
            error={form.formState.errors.actor?.message}
          >
            <TextInput
              placeholder={t('admin:audit.filters.actorPlaceholder')}
              {...form.register('actor', {
                validate: (value) =>
                  value.trim() === '' ||
                  UUID_RE.test(value.trim()) ||
                  t('admin:audit.filters.actorInvalid', {
                    defaultValue: 'Enter a full user id (UUID), not an email.',
                  }),
              })}
            />
          </FormField>
        ) : null}
        <FormField label={t('admin:audit.filters.from')}>
          <TextInput type="datetime-local" {...form.register('from')} />
        </FormField>
        <FormField label={t('admin:audit.filters.to')}>
          <TextInput type="datetime-local" {...form.register('to')} />
        </FormField>
        <div className="flex items-end gap-2 col-span-1 sm:col-span-2 lg:col-span-5">
          <Button type="submit" variant="primary">
            {t('admin:audit.filters.apply')}
          </Button>
          <Button type="button" variant="ghost" onClick={onClearFilters}>
            {t('admin:audit.filters.clear')}
          </Button>
        </div>
      </form>

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {loading && rows.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size={28} />
        </div>
      ) : (
        <Table
          data={rows}
          columns={columns}
          rowKey={(row) => row.id}
          empty={t('admin:audit.table.empty')}
        />
      )}

      <div className="flex items-center justify-end gap-2 text-[12.5px] text-ink-2">
        <Button
          size="sm"
          variant="secondary"
          disabled={offset === 0 || loading}
          onClick={() => void fetchAudit(Math.max(0, offset - PAGE_SIZE), filters)}
        >
          {t('admin:common.previous')}
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={!hasMore || loading}
          onClick={() => void fetchAudit(offset + PAGE_SIZE, filters)}
        >
          {t('admin:common.next')}
        </Button>
      </div>
    </div>
  );
}

function formatDate(iso: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: 'short',
      timeStyle: 'medium',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}
